/**
 * @fileoverview Maps icon name strings to Lucide icon components.
 * Used by interactive tool renderers that receive icon names from the AI.
 */

'use client';

import {
  AlertTriangle,
  BarChart3,
  Building,
  CheckCircle,
  ClipboardList,
  Cloud,
  Coins,
  Database,
  Eye,
  FileText,
  Globe,
  HelpCircle,
  Lock,
  MessageSquare,
  Settings,
  Shield,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  AlertTriangle,
  BarChart3,
  Building,
  CheckCircle,
  ClipboardList,
  Cloud,
  Coins,
  Database,
  Eye,
  FileText,
  Globe,
  HelpCircle,
  Lock,
  MessageSquare,
  Settings,
  Shield,
  ShieldCheck,
  Users,
};

/**
 * Normalizes icon name strings to PascalCase for ICON_MAP lookup.
 * Handles kebab-case ("shield-check") and camelCase ("shieldCheck").
 */
function normalizeIconName(name: string): string {
  // kebab-case → PascalCase
  if (name.includes('-')) {
    return name.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
  }
  // camelCase → PascalCase
  return name.charAt(0).toUpperCase() + name.slice(1);
}

interface InteractiveIconProps {
  name?: string;
  className?: string;
  fallback?: string;
}

/**
 * Renders a Lucide icon from a string name.
 * Falls back to a text emoji if the icon name is not in the map.
 */
export function InteractiveIcon({ name, className = 'size-4', fallback }: InteractiveIconProps) {
  if (!name) {
    return fallback ? <span>{fallback}</span> : null;
  }

  const normalized = normalizeIconName(name);
  const Icon = ICON_MAP[normalized] || ICON_MAP[name];
  if (Icon) {
    return <Icon className={className} />;
  }

  // If it looks like an emoji (starts with non-ASCII), render as-is
  if (name.codePointAt(0) && name.codePointAt(0)! > 127) {
    return <span>{name}</span>;
  }

  return fallback ? <span>{fallback}</span> : null;
}
